import React, { useState, useEffect, useRef } from 'react';
import { useDashboard } from '../DashboardContext';

const InsightBanner: React.FC = () => {
    const { summary, otherActions, loading, selectedInstituteName, timeframe, batchRange } = useDashboard();

    // ── SM editing state ──────────────────────────────────────────────────────
    const [isEditing, setIsEditing]       = useState(false);
    const [isDiscarded, setIsDiscarded]   = useState(false);
    const [isHovered, setIsHovered]       = useState(false);
    const [customHeadline, setCustomHeadline] = useState('');
    const [customSubtext, setCustomSubtext]   = useState('');
    const [isCustomized, setIsCustomized] = useState(false);
    const [draftHeadline, setDraftHeadline] = useState('');
    const [draftSubtext, setDraftSubtext]   = useState('');
    const [showRegenPrompt, setShowRegenPrompt] = useState(false);

    // Track previous period+filter to detect changes
    const prevPeriodRef = useRef({ timeframe, batchRange });

    // When period/filter changes and SM has custom text → show regen prompt
    useEffect(() => {
        const prev = prevPeriodRef.current;
        if ((prev.timeframe !== timeframe || prev.batchRange !== batchRange) && isCustomized) {
            setShowRegenPrompt(true);
        }
        prevPeriodRef.current = { timeframe, batchRange };
    }, [timeframe, batchRange]);

    // When summary loads, reset discard state for new institute/period
    const prevSummaryRef = useRef<any>(null);
    useEffect(() => {
        if (summary && summary !== prevSummaryRef.current) {
            prevSummaryRef.current = summary;
            if (!isCustomized) setIsDiscarded(false);
        }
    }, [summary]);

    // ── Compute auto content from data ───────────────────────────────────────
    const name     = selectedInstituteName || 'This institute';
    const active   = summary?.activeUsers ?? 0;
    const campaigns = summary?.campaignInteractions ?? 0;
    const others   = summary?.otherActions ?? 0;

    const autoHeadline = (() => {
        if (active > 0 && active >= campaigns && active >= others)
            return `${name} saw ${active} active alumni this period`;
        if (campaigns > 0 && campaigns >= others)
            return `${campaigns} campaign interactions recorded for ${name}`;
        if (others > 0)
            return `${others} platform actions logged for ${name} this period`;
        return `${name} — activity summary for this period`;
    })();

    const autoParts: string[] = [];
    if (campaigns > 0) autoParts.push(`${campaigns} campaign interactions`);
    if (otherActions?.platformLogins > 0) autoParts.push(`${otherActions.platformLogins} platform logins`);
    // TEMP: email opens omitted while Email Opens card is hidden
    const autoSubtext = autoParts.length > 0
        ? autoParts.join(' · ')
        : `${active} unique alumni were active on the platform.`;

    const displayHeadline = isCustomized ? customHeadline : autoHeadline;
    const displaySubtext  = isCustomized ? customSubtext  : autoSubtext;

    // ── Chips (top 3 by value) ────────────────────────────────────────────────
    // TEMP: Email Opens / WhatsApp Reads omitted while those Other Actions cards are hidden
    const chips = [
        { label: 'Active Users',          value: active,                          icon: '👥' },
        { label: 'Campaign Interactions', value: campaigns,                        icon: '📧' },
        { label: 'Platform Logins',       value: otherActions?.platformLogins ?? 0, icon: '🔑' },
        { label: 'Other Actions',         value: others,                           icon: '⚡' },
    ]
        .filter(m => m.value > 0)
        .sort((a, b) => b.value - a.value)
        .slice(0, 3);

    // ── Handlers ─────────────────────────────────────────────────────────────
    const openEdit = () => {
        setDraftHeadline(displayHeadline);
        setDraftSubtext(displaySubtext);
        setIsEditing(true);
    };

    const handleSave = () => {
        if (!draftHeadline.trim()) return;
        setCustomHeadline(draftHeadline.trim());
        setCustomSubtext(draftSubtext.trim());
        setIsCustomized(true);
        setIsEditing(false);
    };

    const handleDiscard = () => {
        setIsDiscarded(true);
        setIsEditing(false);
        setIsCustomized(false);
    };

    const handleRestore = () => {
        setIsDiscarded(false);
        setIsCustomized(false);
    };

    const handleRegenYes = () => {
        setIsCustomized(false);
        setCustomHeadline('');
        setCustomSubtext('');
        setShowRegenPrompt(false);
    };

    const handleRegenKeep = () => setShowRegenPrompt(false);

    // ── Loading skeleton ──────────────────────────────────────────────────────
    if (loading) {
        return (
            <div className="rounded mb-3 p-4 placeholder-glow"
                style={{ background: 'linear-gradient(135deg,#1e293b,#0f172a)', minHeight: 88 }}>
                <div className="d-flex justify-content-between align-items-start gap-3">
                    <div className="flex-1">
                        <span className="placeholder col-7 d-block mb-2" style={{ height: 20 }}></span>
                        <span className="placeholder col-9 d-block" style={{ height: 13 }}></span>
                    </div>
                    <div className="d-flex gap-2">
                        {[110, 110, 110].map((w, i) => (
                            <span key={i} className="placeholder rounded-pill" style={{ width: w, height: 26 }}></span>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    if (!summary) return null;

    // ── Discarded state: show restore link ────────────────────────────────────
    if (isDiscarded) {
        return (
            <div className="mb-3 text-center" style={{ opacity: 0.6 }}>
                <button className="btn btn-link btn-sm text-muted p-0" style={{ fontSize: 12 }}
                    onClick={handleRestore}>
                    + Add insight banner
                </button>
            </div>
        );
    }

    return (
        <div className="rounded mb-3"
            style={{ background: 'linear-gradient(135deg,#1e293b,#0f172a)', color: '#f8fafc', position: 'relative' }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            {/* Regeneration prompt (period/filter changed with custom text) */}
            {showRegenPrompt && (
                <div className="px-4 pt-3 pb-2 d-flex align-items-center gap-3 flex-wrap border-bottom border-secondary">
                    <span className="small" style={{ color: '#94a3b8' }}>
                        Period/filter changed. Regenerate insight?
                    </span>
                    <button className="btn btn-sm btn-outline-light py-0 px-2" style={{ fontSize: 12 }}
                        onClick={handleRegenYes}>Yes</button>
                    <button className="btn btn-sm btn-link text-secondary py-0 px-1" style={{ fontSize: 12 }}
                        onClick={handleRegenKeep}>Keep current</button>
                </div>
            )}

            {/* Main banner content */}
            {!isEditing ? (
                <div className="p-4 d-flex justify-content-between align-items-start gap-3 flex-wrap">
                    {/* Left: icon + headline + subtext */}
                    <div className="flex-1" style={{ minWidth: 0 }}>
                        <div className="d-flex align-items-center gap-2 mb-1">
                            <span style={{ fontSize: 22, flexShrink: 0 }}>✨</span>
                            <div className="fw-bold" style={{ fontSize: 15, lineHeight: 1.3 }}>
                                {displayHeadline}
                            </div>
                        </div>
                        <div className="ms-1" style={{ color: '#94a3b8', fontSize: 12 }}>
                            {displaySubtext}
                            {isCustomized && (
                                <span className="ms-2 badge" style={{ background: '#334155', fontSize: 10 }}>
                                    Edited
                                </span>
                            )}
                        </div>
                    </div>

                    {/* Right: chips + edit button */}
                    <div className="d-flex flex-wrap gap-2 align-items-center flex-shrink-0">
                        {chips.map((chip, i) => (
                            <span key={i} className="badge rounded-pill px-3 py-1 d-flex align-items-center gap-1"
                                style={{ background: '#334155', color: '#e2e8f0', fontSize: 11, fontWeight: 500 }}>
                                <span>{chip.icon}</span>
                                <span>{chip.value.toLocaleString()} {chip.label}</span>
                            </span>
                        ))}
                        {/* Pencil edit icon — on hover (always on small screens) */}
                        <button
                            className="btn btn-sm btn-outline-light py-0 px-2 d-md-block"
                            style={{
                                fontSize: 12,
                                opacity: isHovered ? 1 : 0,
                                transition: 'opacity 0.15s',
                            }}
                            onClick={openEdit}
                            title="Edit insight banner"
                        >
                            ✏️
                        </button>
                    </div>
                </div>
            ) : (
                /* Inline edit panel — inside the banner (not modal) */
                <div className="p-4">
                    <div className="mb-2">
                        <label className="small fw-semibold mb-1" style={{ color: '#94a3b8' }}>
                            Headline <span style={{ color: '#64748b' }}>(max 80 chars)</span>
                        </label>
                        <input
                            className="form-control form-control-sm"
                            style={{ background: '#334155', color: '#f8fafc', border: '1px solid #475569' }}
                            maxLength={80}
                            value={draftHeadline}
                            onChange={e => setDraftHeadline(e.target.value)}
                            autoFocus
                        />
                        <div className="text-end small mt-1" style={{ color: '#64748b' }}>
                            {draftHeadline.length}/80
                        </div>
                    </div>
                    <div className="mb-3">
                        <label className="small fw-semibold mb-1" style={{ color: '#94a3b8' }}>
                            Sub-text <span style={{ color: '#64748b' }}>(max 140 chars)</span>
                        </label>
                        <textarea
                            className="form-control form-control-sm"
                            style={{ background: '#334155', color: '#f8fafc', border: '1px solid #475569', resize: 'none' }}
                            rows={2}
                            maxLength={140}
                            value={draftSubtext}
                            onChange={e => setDraftSubtext(e.target.value)}
                        />
                        <div className="text-end small mt-1" style={{ color: '#64748b' }}>
                            {draftSubtext.length}/140
                        </div>
                    </div>
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                        <button className="btn btn-sm btn-outline-secondary py-0 px-2"
                            style={{ fontSize: 12, color: '#94a3b8', borderColor: '#475569' }}
                            disabled title="AI generation not configured">
                            🤖 Rewrite with AI
                        </button>
                        <button className="btn btn-sm btn-outline-light py-0 px-3"
                            style={{ fontSize: 12 }}
                            onClick={handleSave}
                            disabled={!draftHeadline.trim()}>
                            Save
                        </button>
                        <button className="btn btn-sm btn-link py-0 px-1"
                            style={{ fontSize: 12, color: '#64748b' }}
                            onClick={() => setIsEditing(false)}>
                            Cancel
                        </button>
                        <button className="btn btn-sm btn-link py-0 ms-auto"
                            style={{ fontSize: 12, color: '#ef4444' }}
                            onClick={handleDiscard}>
                            Discard / Remove Banner
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default InsightBanner;
